import { useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cityClassifiedCategories, australianCities, regions } from "@shared/schema";
import { ArrowLeft, MapPin, Phone, Mail, Globe, Image as ImageIcon } from "lucide-react";
import { z } from "zod";
import { MarketImageUploader } from "@/components/MarketImageUploader";

const formSchema = z.object({
  title: z.string().min(5, "제목은 최소 5자 이상이어야 합니다"),
  description: z.string().min(20, "설명은 최소 20자 이상이어야 합니다"),
  category: z.string(),
  subcategory: z.string().optional(),
  state: z.string(),
  city: z.string(),
  suburb: z.string().optional(),
  contactMethod: z.string(),
  phoneNumber: z.string().optional(),
  email: z.string().optional(),
  websiteUrl: z.string().optional(),
  images: z.array(z.string()).default([]),
  expiresAt: z.date(),
});

export default function CityClassifiedRegister() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState<string>("");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "구인구직",
      state: "",
      city: "",
      suburb: "",
      contactMethod: "phone",
      phoneNumber: "",
      email: user?.email || "",
      websiteUrl: "",
      images: [],
  expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30일 후
  },
});

  type CityClassifiedPayload = {
    userId: string;
    userEmail: string;
    userDisplayName: string;
    userPhotoUrl?: string;
    title: string;
    description: string;
    category: string;
    subcategory?: string | null;
    state: string;
    city: string;
    suburb?: string | null;
    contactMethod: string;
    phoneNumber?: string | null;
    email?: string | null;
    websiteUrl?: string | null;
    images: string[];
    isPremium: boolean;
    expiresAt: Date;
    premiumUntil?: Date | null;
  };

  const createMutation = useMutation<unknown, Error, CityClassifiedPayload>({
    mutationFn: async (data) => {
      return await apiRequest("POST", "/api/city-classifieds", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/city-classifieds"] });
      toast({
        title: "광고 등록 완료",
        description: "도시별 개인광고가 성공적으로 등록되었습니다.",
      });
      setLocation("/");
    },
    onError: (error: Error) => {
      toast({
        title: "등록 실패",
        description: error.message || "광고 등록 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (!user) {
      toast({
        title: "로그인 필요",
        description: "광고를 등록하려면 로그인이 필요합니다.",
        variant: "destructive",
      });
      return;
    }

    const expiresAt =
      data.expiresAt ? new Date(data.expiresAt) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    const payload: CityClassifiedPayload = {
      userId: user.uid,
      userEmail: user.email || "",
      userDisplayName: user.displayName || "",
      userPhotoUrl: user.photoURL || undefined,
      title: data.title,
      description: data.description,
      category: data.category,
      subcategory: data.subcategory,
      state: data.state,
      city: data.city,
      suburb: data.suburb,
      contactMethod: data.contactMethod,
      phoneNumber: data.phoneNumber || null,
      email: data.email || null,
      websiteUrl: data.websiteUrl || null,
      images: data.images,
      isPremium: false,
      expiresAt,
      premiumUntil: undefined,
    };

    createMutation.mutate(payload);
  };

  const availableCities = selectedState && selectedState in australianCities
    ? australianCities[selectedState as keyof typeof australianCities]
    : [];

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>로그인 필요</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              광고를 등록하려면 로그인이 필요합니다.
            </p>
            <Button onClick={() => setLocation("/")} className="w-full">
              홈으로 돌아가기
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-3xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => setLocation("/")}
              className="mb-4"
              data-testid="button-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              목록으로
            </Button>
            <h1 className="text-3xl font-bold" data-testid="text-title">
              도시별 개인광고 등록
            </h1>
            <p className="text-muted-foreground mt-2">
              지역 커뮤니티에 서비스, 레슨, 이벤트 등을 홍보하세요
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>광고 정보</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>제목 *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="예: 영어 과외 레슨 (시드니 지역)"
                            {...field}
                            data-testid="input-title"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Category */}
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>카테고리 *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-category">
                              <SelectValue placeholder="카테고리 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {cityClassifiedCategories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>상세 설명 *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="광고 내용을 자세히 작성해주세요..."
                            className="min-h-[150px]"
                            {...field}
                            data-testid="input-description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Images Section */}
                  <div className="space-y-4 p-4 border rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-5 h-5" />
                      <h3 className="font-semibold">사진 (선택)</h3>
                    </div>
                    
                    <MarketImageUploader
                      maxImages={3}
                      onImagesChange={(paths) => form.setValue("images", paths)}
                      userId={user?.uid}
                    />
                    
                    <FormDescription className="text-xs">
                      광고와 관련된 사진을 최대 3장까지 등록할 수 있어요.
                      <br />
                      첫 번째 사진이 대표 이미지로 보여져요.
                    </FormDescription>
                  </div>

                  {/* Location Section */}
                  <div className="space-y-4 p-4 border rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5" />
                      <h3 className="font-semibold">위치 정보</h3>
                    </div>

                    {/* State */}
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>주/지역 *</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedState(value);
                              form.setValue("city", "");
                            }}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="select-state">
                                <SelectValue placeholder="주 선택" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {regions.filter(r => r.id !== "ALL").map((region) => (
                                <SelectItem key={region.id} value={region.id}>
                                  {region.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* City */}
                    {availableCities.length > 0 && (
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>도시 *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-city">
                                  <SelectValue placeholder="도시 선택" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableCities.map((city) => (
                                  <SelectItem key={city.id} value={city.id}>
                                    {city.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Suburb */}
                    <FormField
                      control={form.control}
                      name="suburb"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>서버브 (선택)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="예: Strathfield, Burwood 등"
                              {...field}
                              value={field.value || ""}
                              data-testid="input-suburb"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Contact Section */}
                  <div className="space-y-4 p-4 border rounded-md">
                    <h3 className="font-semibold mb-2">연락처 정보</h3>

                    {/* Contact Method */}
                    <FormField
                      control={form.control}
                      name="contactMethod"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>연락 방법 *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-contact-method">
                                <SelectValue placeholder="연락 방법 선택" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="phone">전화</SelectItem>
                              <SelectItem value="email">이메일</SelectItem>
                              <SelectItem value="both">전화 + 이메일</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Phone Number */}
                    {(form.watch("contactMethod") === "phone" || form.watch("contactMethod") === "both") && (
                      <FormField
                        control={form.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>전화번호 *</FormLabel>
                            <FormControl>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-muted-foreground" />
                                <Input
                                  placeholder="예: 0412 345 678"
                                  {...field}
                                  value={field.value || ""}
                                  data-testid="input-phone"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Email */}
                    {(form.watch("contactMethod") === "email" || form.watch("contactMethod") === "both") && (
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>이메일 *</FormLabel>
                            <FormControl>
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-muted-foreground" />
                                <Input
                                  type="email"
                                  placeholder="example@email.com"
                                  {...field}
                                  value={field.value || ""}
                                  data-testid="input-email"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Website URL */}
                    <FormField
                      control={form.control}
                      name="websiteUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>웹사이트 (선택)</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-muted-foreground" />
                              <Input
                                type="url"
                                placeholder="https://..."
                                {...field}
                                value={field.value || ""}
                                data-testid="input-website"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setLocation("/")}
                      className="flex-1"
                      data-testid="button-cancel"
                    >
                      취소
                    </Button>
                    <Button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="flex-1"
                      data-testid="button-submit"
                    >
                      {createMutation.isPending ? "등록 중..." : "광고 등록"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
